import { useState, useRef, useEffect } from 'react';
import { 
  X, Globe, FileSpreadsheet, Upload, ChevronRight, ChevronLeft,
  Check, AlertCircle, Loader, Package, DollarSign, Tag, Eye, RotateCw, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiFetch, API } from './api';

const styles = `
  .im-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: im-fadein 0.25s ease;
  }
  @keyframes im-fadein { from { opacity:0 } to { opacity:1 } }

  .im-modal {
    background: #1e293b;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 20px;
    width: 90%;
    max-width: 680px;
    max-height: 88vh;
    overflow-y: auto;
    animation: im-slide 0.25s ease;
  }
  @keyframes im-slide { from { opacity:0; transform:translateY(16px) } to { opacity:1; transform:translateY(0) } }

  .im-header {
    padding: 24px 28px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .im-header h2 {
    font-family: 'Cormorant Garamond', serif;
    font-size: 22px;
    font-weight: 600;
    color: #f5f0e8;
    margin: 0;
  }

  .im-close {
    background: none;
    border: none;
    color: rgba(245,240,232,0.4);
    cursor: pointer;
    padding: 4px;
  }
  .im-close:hover { color: #f5f0e8; }

  .im-body { padding: 24px 28px; }

  /* Method Selection */
  .im-method {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 14px;
    padding: 20px;
    margin-bottom: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 16px;
    transition: all 0.15s;
  }
  .im-method:hover { background: rgba(255,255,255,0.06); border-color: rgba(255,255,255,0.15); }

  .im-method-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .im-method-title {
    font-size: 15px;
    font-weight: 700;
    color: #f5f0e8;
    margin-bottom: 3px;
  }

  .im-method-desc { font-size: 12px; color: rgba(245,240,232,0.45); }

  .im-method-arrow { color: rgba(245,240,232,0.3); margin-left: auto; }

  /* URL Input */
  .im-input-group { margin-bottom: 20px; }

  .im-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: rgba(245,240,232,0.5);
    margin-bottom: 6px;
  }

  .im-input {
    width: 100%;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 10px;
    padding: 12px 16px;
    color: #f5f0e8;
    font-size: 14px;
    font-family: 'Nunito Sans', sans-serif;
  }
  .im-input:focus { outline: none; border-color: #c9a84c; }

  .im-hint {
    font-size: 11px;
    color: rgba(245,240,232,0.35);
    margin-top: 6px;
  }

  /* Up-front import fee table (shown before the crawl starts) */
  .im-fee-table {
    margin-top: 18px;
    padding: 16px 18px;
    border-radius: 12px;
    background: rgba(201,168,76,0.07);
    border: 1px solid rgba(201,168,76,0.22);
  }
  .im-fee-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #e8dfc8;
  }
  .im-fee-exempt {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #a7ede4;
  }
  .im-fee-list {
    list-style: none;
    margin: 12px 0 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px 20px;
  }
  .im-fee-list li {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    font-size: 12.5px;
    color: rgba(245,240,232,0.72);
    border-bottom: 1px dotted rgba(245,240,232,0.12);
    padding-bottom: 4px;
  }
  .im-fee-list strong { color: #c9a84c; font-size: 13px; }
  .im-fee-foot {
    margin: 12px 0 0;
    font-size: 12px;
    line-height: 1.55;
    color: rgba(245,240,232,0.72);
  }
  @media (max-width: 520px) {
    .im-fee-list { grid-template-columns: 1fr; }
  }


  /* File upload zone */
  .im-dropzone {
    border: 2px dashed rgba(255,255,255,0.15);
    border-radius: 14px;
    padding: 32px;
    text-align: center;
    cursor: pointer;
    transition: all 0.15s;
    margin-bottom: 16px;
  }
  .im-dropzone:hover { border-color: #c9a84c; background: rgba(201,168,76,0.04); }
  .im-dropzone.has-file { border-color: #22c55e; background: rgba(34,197,94,0.04); }

  /* Product preview table */
  .im-preview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .im-count {
    font-size: 13px;
    color: rgba(245,240,232,0.5);
  }

  .im-products {
    max-height: 340px;
    overflow-y: auto;
  }

  .im-product {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .im-product-check {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    border: 2px solid rgba(255,255,255,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s;
  }
  .im-product-check.on { background: #c9a84c; border-color: #c9a84c; }

  .im-product-info { flex: 1; min-width: 0; }

  .im-product-title {
    font-size: 14px;
    font-weight: 600;
    color: #f5f0e8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .im-product-meta {
    font-size: 12px;
    color: rgba(245,240,232,0.4);
    display: flex;
    gap: 12px;
    margin-top: 2px;
  }

  .im-product-price {
    font-size: 14px;
    font-weight: 700;
    color: #c9a84c;
    flex-shrink: 0;
  }

  /* Footer */
  .im-footer {
    padding: 16px 28px;
    border-top: 1px solid rgba(255,255,255,0.08);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  /* Loading */
  .im-loading {
    text-align: center;
    padding: 40px 0;
  }

  .im-loading p {
    color: rgba(245,240,232,0.5);
    font-size: 14px;
    margin-top: 16px;
  }

  .im-spinner {
    animation: im-spin 1s linear infinite;
  }
  @keyframes im-spin { to { transform: rotate(360deg) } }

  .im-error {
    background: rgba(239,68,68,0.1);
    border: 1px solid rgba(239,68,68,0.2);
    border-radius: 10px;
    padding: 14px 16px;
    color: #fca5a5;
    font-size: 13px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .im-error-msg { flex: 1; line-height: 1.5; }
  .im-retry {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 13px;
    border-radius: 999px;
    border: 1px solid rgba(252,165,165,0.4);
    background: rgba(239,68,68,0.14);
    color: #fecaca;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
  }
  .im-retry:hover {
    background: rgba(239,68,68,0.26);
    border-color: rgba(252,165,165,0.7);
    transform: translateY(-1px);
  }


  .im-success {
    text-align: center;
    padding: 32px 0;
  }

  .im-success-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, #22c55e, #16a34a);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
  }
`;

// Fallback only — the live tiers come from the backend's /import/fee `tier_breakdown`.
const FEE_TIERS = [
  { label: 'Up to 50 products', fee: '$2.00' },
  { label: 'Up to 200 products', fee: '$5.00' },
  { label: 'Up to 500 products', fee: '$10.00' },
  { label: 'More than 500 products', fee: '$15.00' },
];

const tierLabel = (upTo) =>
  String(upTo).includes('+') ? `More than ${String(upTo).replace('+', '')} products` : `Up to ${upTo} products`;

export default function ProductImportModal({ onClose, onImportComplete }) {
  const [step, setStep] = useState('choose'); // choose, website, csv, preview, importing, done
  const [url, setUrl] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [products, setProducts] = useState([]);
  // Which platform we read, and anything the seller needs to know about it (e.g. Wix keeps
  // its size/colour options private, so those products arrive as single items).
  const [platformInfo, setPlatformInfo] = useState({ platform: null, notice: null, duplicates: 0 });
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importedCount, setImportedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [importStatus, setImportStatus] = useState('');
  const [importFee, setImportFee] = useState(null); // { fee, is_exempt }
  const [feeIsExempt, setFeeIsExempt] = useState(false);
  const [feeTiers, setFeeTiers] = useState(FEE_TIERS);

  const fileRef = useRef();

  // Learn up front whether this account pays an import fee, and the live tier table.
  useEffect(() => {
    apiFetch('/vendor/products/import/fee?product_count=1')
      .then(d => {
        setFeeIsExempt(!!d?.is_exempt);
        if (Array.isArray(d?.tier_breakdown) && d.tier_breakdown.length) {
          setFeeTiers(d.tier_breakdown.map(t => ({
            label: tierLabel(t.up_to),
            fee: `$${Number(t.fee).toFixed(2)}`,
          })));
        }
      })
      .catch(() => setFeeIsExempt(false));
  }, []);

  const fetchImportFee = async (count) => {
    try {
      const feeData = await apiFetch(`/vendor/products/import/fee?product_count=${count}`);
      setImportFee(feeData);
    } catch {
      setImportFee({ fee: 0, is_exempt: false });
    }
  };

  const handleWebsiteImport = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError('');
    try {
      // Start the async import job
      const startData = await apiFetch('/vendor/products/import/website', {
        method: 'POST',
        body: JSON.stringify({ url: url.trim(), max_products: 1000 }),
      });

      const jobId = startData.job_id;
      if (!jobId) throw new Error('Failed to start import');

      // Poll for completion
      const statusMessages = {
        started: 'Starting the import...',
        crawling: 'Asking your store for its catalogue...',
        analyzing: 'Reading your products...',
        downloading_images: 'Downloading product images...',
      };

      let result = null;
      for (let i = 0; i < 120; i++) {  // Poll for up to 10 minutes
        await new Promise(r => setTimeout(r, 5000));
        const status = await apiFetch(`/vendor/products/import/website/status/${jobId}`);

        if (status.status === 'complete') {
          result = status;
          break;
        }
        // Update loading message with live status and product count
        const baseMsg = statusMessages[status.status] || 'Processing...';
        const count = status.products_found || 0;
        const detail = status.detail || '';
        const msg = count > 0
          ? `${detail || baseMsg}`
          : baseMsg;
        setImportStatus(msg);
      }

      if (!result) throw new Error('Import timed out — please try again');

      const found = result.products || [];
      // Anything the vendor already sells starts unticked. A second import of the same store
      // is normal — a throttled store hands its catalogue over in pieces — and nobody should
      // have to spot the repeats in a list of fifty.
      const fresh = found.filter(p => !p.already_imported);
      setProducts(found);
      setPlatformInfo({
        platform: result.platform || null,
        notice: result.notice || null,
        duplicates: result.duplicates || 0,
      });
      setSelected(new Set(fresh.map(p => p.id)));
      await fetchImportFee(fresh.length);
      setStep('preview');
    } catch (err) {
      setError(err.message || 'Failed to import from website');
    } finally {
      setLoading(false);
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile) return;
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/vendor/products/import/csv`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Upload failed');
      }
      
      const data = await res.json();
      setProducts(data.products || []);
      setSelected(new Set((data.products || []).map(p => p.id)));
      await fetchImportFee((data.products || []).length);
      setStep('preview');
    } catch (err) {
      setError(err.message || 'Failed to parse CSV');
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    // "Select all" never re-ticks something the vendor already sells.
    const fresh = products.filter(p => !p.already_imported);
    if (selected.size >= fresh.length) setSelected(new Set());
    else setSelected(new Set(fresh.map(p => p.id)));
  };

  // Recalculate fee when selection changes
  useEffect(() => {
    if (step === 'preview' && selected.size > 0) {
      fetchImportFee(selected.size);
    }
  }, [selected.size, step]);

  const handleConfirm = async () => {
    const toImport = products.filter(p => selected.has(p.id));
    if (!toImport.length) return;
    
    setStep('importing');
    setError('');
    try {
      const data = await apiFetch('/vendor/products/import/confirm', {
        method: 'POST',
        body: JSON.stringify({ products: toImport }),
      });
      setImportedCount(data.created || 0);
      setSkippedCount(data.skipped || 0);
      setStep('done');
    } catch (err) {
      setError(err.message || 'Failed to create products');
      setStep('preview');
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="im-overlay">
        <div className="im-modal">
          <div className="im-header">
            <h2>
              {step === 'choose' && 'Import Products'}
              {step === 'website' && 'Import from Website'}
              {step === 'csv' && 'Import from CSV'}
              {step === 'preview' && 'Review Products'}
              {step === 'importing' && 'Importing...'}
              {step === 'done' && 'Import Complete'}
            </h2>
            <button className="im-close" onClick={onClose} data-testid="import-close">
              <X size={20} />
            </button>
          </div>

          <div className="im-body">
            {error && (
              <div className="im-error" data-testid="import-error">
                <AlertCircle size={16} style={{ flexShrink: 0 }} />
                <span className="im-error-msg">{error}</span>
                {step === 'website' && url.trim() && !loading && (
                  <button
                    type="button"
                    className="im-retry"
                    onClick={handleWebsiteImport}
                    data-testid="import-retry-btn"
                  >
                    <RotateCw size={13} /> Try again
                  </button>
                )}
              </div>
            )}

            {/* Step: Choose Method */}
            {step === 'choose' && (
              <>
                <div className="im-method" onClick={() => setStep('website')} data-testid="import-website-btn">
                  <div className="im-method-icon" style={{ background: 'rgba(34,211,238,0.12)' }}>
                    <Globe size={22} color="#22d3ee" />
                  </div>
                  <div>
                    <div className="im-method-title">AI Website Import</div>
                    <div className="im-method-desc">Paste your website URL and AI will extract your products automatically</div>
                  </div>
                  <ChevronRight size={18} className="im-method-arrow" />
                </div>

                <div className="im-method" onClick={() => setStep('csv')} data-testid="import-csv-btn">
                  <div className="im-method-icon" style={{ background: 'rgba(34,197,94,0.12)' }}>
                    <FileSpreadsheet size={22} color="#22c55e" />
                  </div>
                  <div>
                    <div className="im-method-title">CSV Bulk Upload</div>
                    <div className="im-method-desc">Upload a spreadsheet with columns: Title, Description, Price, Type, Category</div>
                  </div>
                  <ChevronRight size={18} className="im-method-arrow" />
                </div>
              </>
            )}

            {/* Step: Website URL */}
            {step === 'website' && !loading && (
              <>
                <div className="im-input-group">
                  <div className="im-label">Website URL</div>
                  <input
                    className="im-input"
                    placeholder="https://yourshop.com/products"
                    value={url}
                    onChange={e => setUrl(e.target.value)}
                    data-testid="import-url-input"
                  />
                  <p className="im-hint">
                    Enter the URL of the page that lists your products. For best results, use your main shop or products page.
                  </p>
                  <p className="im-hint" data-testid="import-platform-hint">
                    If your shop runs on <strong>Shopify, Ecwid or Square Online</strong>, we read
                    your catalogue straight from it — exact names, prices, options and photos.
                    Any other shop is read off the page instead.
                  </p>
                </div>

                {/* Fee shown BEFORE the crawl starts — the exact amount is confirmed again on the review step */}
                <div className="im-fee-table" data-testid="import-fee-upfront">
                  {feeIsExempt ? (
                    <div className="im-fee-exempt">
                      <DollarSign size={16} color="#2dd4bf" />
                      <span>No import fee applies to your account.</span>
                    </div>
                  ) : (
                    <>
                      <div className="im-fee-title">
                        <DollarSign size={16} color="#c9a84c" />
                        <span>Import fee, based on how many products you import</span>
                      </div>
                      <ul className="im-fee-list">
                        {feeTiers.map(t => (
                          <li key={t.label}>
                            <span>{t.label}</span>
                            <strong>{t.fee}</strong>
                          </li>
                        ))}
                      </ul>
                      <p className="im-fee-foot">
                        You'll see the exact amount, and choose which products to keep, before anything is imported.
                        The fee is deducted from your next payout.
                      </p>
                    </>
                  )}
                </div>
              </>
            )}

            {/* Step: CSV Upload */}
            {step === 'csv' && !loading && (
              <>
                <div
                  className={`im-dropzone ${csvFile ? 'has-file' : ''}`}
                  onClick={() => fileRef.current?.click()}
                  data-testid="import-csv-dropzone"
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={e => setCsvFile(e.target.files[0])}
                  />
                  {csvFile ? (
                    <>
                      <FileSpreadsheet size={28} color="#22c55e" />
                      <p style={{ color: '#f5f0e8', fontSize: 14, fontWeight: 600, marginTop: 8 }}>{csvFile.name}</p>
                      <p style={{ color: 'rgba(245,240,232,0.4)', fontSize: 12 }}>Click to change file</p>
                    </>
                  ) : (
                    <>
                      <Upload size={28} color="rgba(245,240,232,0.3)" />
                      <p style={{ color: 'rgba(245,240,232,0.5)', fontSize: 14, marginTop: 8 }}>Click to upload CSV</p>
                      <p style={{ color: 'rgba(245,240,232,0.3)', fontSize: 12 }}>Columns: Title, Description, Price, Type, Category, Tags</p>
                    </>
                  )}
                </div>
                <button
                  onClick={() => {
                    const template = 'Title,Description,Price,Type,Category,Tags\nAmethyst Cluster,"Beautiful healing amethyst cluster for meditation",45.00,physical,Crystals & Minerals,"amethyst,healing,meditation"\nSacred Geometry Art Print,"Original digital art for contemplation",19.99,digital,Art,"art,sacred-geometry,print"\n';
                    const blob = new Blob([template], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'product_import_template.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  style={{
                    background: 'none', border: 'none', color: '#22d3ee',
                    fontSize: 13, cursor: 'pointer', textDecoration: 'underline',
                    fontFamily: "'Nunito Sans', sans-serif", padding: 0,
                  }}
                  data-testid="import-csv-template-btn"
                >
                  Download CSV Template
                </button>
              </>
            )}

            {/* Loading */}
            {loading && (
              <div className="im-loading">
                <Loader size={32} color="#c9a84c" className="im-spinner" />
                <p data-testid="import-status-msg">
                  {step === 'website' 
                    ? (importStatus || 'Starting AI website scanner...') 
                    : 'Parsing your CSV...'}
                </p>
              </div>
            )}

            {/* Step: Preview */}
            {step === 'preview' && (
              <>
                <div className="im-preview-header">
                  <div className="im-count">
                    {selected.size} of {products.length} selected
                    {platformInfo.platform && (
                      <span style={{ color: 'rgba(245,240,232,0.45)', marginLeft: 8 }}
                            data-testid="import-platform-label">
                        · read from {platformInfo.platform}
                      </span>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={toggleAll} className="text-gray-400 text-xs">
                    {selected.size >= products.filter(p => !p.already_imported).length
                      ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>

                {platformInfo.duplicates > 0 && (
                  <div style={{
                    margin: '0 0 12px', padding: '12px 16px', borderRadius: 10,
                    background: 'rgba(200,170,90,0.08)',
                    border: '1px solid rgba(200,170,90,0.22)',
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    fontSize: 13, lineHeight: 1.55, color: 'rgba(245,240,232,0.8)',
                  }} data-testid="import-duplicates-notice">
                    <Check size={16} color="#d4b76a" style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>
                      {platformInfo.duplicates} of these {platformInfo.duplicates === 1 ? 'is' : 'are'}
                      {' '}already in your store, so {platformInfo.duplicates === 1 ? 'it is' : 'they are'}
                      {' '}left unticked — you will not be charged for them and they cannot be
                      added twice.
                    </span>
                  </div>
                )}

                {platformInfo.notice && (
                  <div style={{
                    margin: '0 0 12px', padding: '12px 16px', borderRadius: 10,
                    background: 'rgba(120,160,220,0.08)',
                    border: '1px solid rgba(120,160,220,0.22)',
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    fontSize: 13, lineHeight: 1.55, color: 'rgba(245,240,232,0.8)',
                  }} data-testid="import-platform-notice">
                    <Info size={16} color="#8fb3e0" style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>{platformInfo.notice}</span>
                  </div>
                )}

                {/* Import Fee Notice */}
                {importFee && (
                  <div style={{
                    margin: '0 0 12px',
                    padding: '12px 16px',
                    borderRadius: 10,
                    background: importFee.is_exempt ? 'rgba(45,212,191,0.08)' : 'rgba(201,168,76,0.08)',
                    border: `1px solid ${importFee.is_exempt ? 'rgba(45,212,191,0.2)' : 'rgba(201,168,76,0.2)'}`,
                    display: 'flex', alignItems: 'center', gap: 10,
                    fontSize: 13,
                  }} data-testid="import-fee-notice">
                    <DollarSign size={16} color={importFee.is_exempt ? '#2dd4bf' : '#c9a84c'} />
                    {importFee.is_exempt ? (
                      <span style={{ color: '#2dd4bf' }}>Admin — import is free</span>
                    ) : (
                      <span style={{ color: 'rgba(245,240,232,0.8)' }}>
                        Import fee: <strong style={{ color: '#c9a84c' }}>${importFee.fee.toFixed(2)}</strong>
                        <span style={{ color: 'rgba(245,240,232,0.4)', marginLeft: 6 }}>
                          — deducted from your next payout
                        </span>
                      </span>
                    )}
                  </div>
                )}
                <div className="im-products">
                  {products.map(p => (
                    <div key={p.id} className="im-product">
                      <div
                        className={`im-product-check ${selected.has(p.id) ? 'on' : ''}`}
                        onClick={() => toggleProduct(p.id)}
                      >
                        {selected.has(p.id) && <Check size={12} color="white" />}
                      </div>
                      {p.images?.length > 0 ? (
                        <img src={p.images[0]} alt="" style={{
                          width: 40, height: 40, borderRadius: 6, objectFit: "cover",
                          border: "1px solid rgba(255,255,255,0.1)", flexShrink: 0,
                        }} />
                      ) : (
                        <div style={{
                          width: 40, height: 40, borderRadius: 6, flexShrink: 0,
                          background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <Package size={16} color="rgba(245,240,232,0.2)" />
                        </div>
                      )}
                      <div className="im-product-info">
                        <div className="im-product-title">{p.title}</div>
                        <div className="im-product-meta">
                          <span>{p.type}</span>
                          {p.category && <span>{p.category}</span>}
                          {p.already_imported && (
                            <span style={{ color: '#d4b76a' }}
                                  data-testid={`import-duplicate-${p.id}`}>
                              already in your store
                            </span>
                          )}
                          {p.variants?.length > 0 && (
                            <span style={{ color: '#c9a84c' }} data-testid="import-variant-count">
                              {p.variants.length} {p.variants.length === 1 ? 'type' : 'types'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="im-product-price">
                        {p.price > 0 ? `$${p.price.toFixed(2)}` : 'No price'}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Step: Importing */}
            {step === 'importing' && (
              <div className="im-loading">
                <Loader size={32} color="#c9a84c" className="im-spinner" />
                <p>Creating {selected.size} products as drafts...</p>
              </div>
            )}

            {/* Step: Done */}
            {step === 'done' && (
              <div className="im-success">
                <div className="im-success-icon">
                  <Check size={28} color="white" />
                </div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: '#f5f0e8', margin: '0 0 8px' }}>
                  {importedCount} Products Imported!
                </h3>
                <p style={{ color: 'rgba(245,240,232,0.5)', fontSize: 14, margin: '0 0 8px' }}>
                  All products were imported as drafts. Review and publish them from your Products page.
                </p>
                {skippedCount > 0 && (
                  <p style={{ color: 'rgba(212,183,106,0.8)', fontSize: 13, margin: '0 0 8px' }}
                     data-testid="import-skipped-note">
                    {skippedCount} {skippedCount === 1 ? 'product was' : 'products were'} already
                    in your store, so {skippedCount === 1 ? 'it was' : 'they were'} skipped and
                    not charged for.
                  </p>
                )}
                {importFee && !importFee.is_exempt && importFee.fee > 0 && (
                  <p style={{ color: 'rgba(201,168,76,0.7)', fontSize: 13, margin: '0 0 24px' }} data-testid="import-fee-charged">
                    ${importFee.fee.toFixed(2)} import fee will be deducted from your next payout.
                  </p>
                )}
                {importFee?.is_exempt && (
                  <p style={{ color: 'rgba(45,212,191,0.6)', fontSize: 13, margin: '0 0 24px' }}>
                    No charge — admin import.
                  </p>
                )}
                <Button
                  onClick={() => { onImportComplete(); onClose(); }}
                  className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white"
                  data-testid="import-done-btn"
                >
                  Go to Products
                </Button>
              </div>
            )}
          </div>

          {/* Footer */}
          {(step === 'website' || step === 'csv' || step === 'preview') && !loading && (
            <div className="im-footer">
              <Button
                variant="ghost"
                onClick={() => { setStep('choose'); setError(''); setProducts([]); }}
                className="text-gray-400"
              >
                <ChevronLeft size={16} className="mr-1" /> Back
              </Button>
              {step === 'website' && (
                <Button
                  onClick={handleWebsiteImport}
                  disabled={!url.trim()}
                  className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white"
                  data-testid="import-scan-btn"
                >
                  <Globe size={16} className="mr-2" /> Scan Website
                </Button>
              )}
              {step === 'csv' && (
                <Button
                  onClick={handleCsvImport}
                  disabled={!csvFile}
                  className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white"
                  data-testid="import-parse-btn"
                >
                  <FileSpreadsheet size={16} className="mr-2" /> Parse CSV
                </Button>
              )}
              {step === 'preview' && (
                <Button
                  onClick={handleConfirm}
                  disabled={selected.size === 0}
                  className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white"
                  data-testid="import-confirm-btn"
                >
                  Import {selected.size} Products
                  {importFee && !importFee.is_exempt && importFee.fee > 0 && (
                    <span style={{ marginLeft: 6, opacity: 0.8 }}>· ${importFee.fee.toFixed(2)}</span>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
